import React from 'react'
import { useParams, Link, useLocation } from 'react-router-dom'
import useSWR from 'swr'

import { getAdminUserInvoices, getAdminUser } from '../../api'
import Spinner from '../../components/ui/spinner'
import { useAuth } from '../../context/auth'
import { formatDateShort } from '../../lib/dates'

const AdminUserInvoices = () => {
  const { user: currentUser } = useAuth()
  const { userId } = useParams()
  const location = useLocation()
  const userEmail = (location.state as any)?.userEmail as string | undefined

  const { data: user, isLoading: isLoadingUser } = useSWR(
    userId ? `/api/admin/users/${userId}` : null,
    getAdminUser
  )
  const { data: invoices, isLoading: isLoadingInvoices } = useSWR(
    userId ? `/api/admin/users/${userId}/invoices` : null,
    getAdminUserInvoices
  )

  const isLoading = isLoadingUser || isLoadingInvoices
  const business = user?.businessDetails

  if (!userId) {
    return <div>User ID required.</div>
  }

  return (
    <div>
      <p className="mb-3">
        <Link to="/admin" className="btn btn-link p-0">← Back to Admin</Link>
      </p>
      <h4 className="mb-2">{userEmail ? `${userEmail}` : `(user ${userId})`}</h4>
      {business && (
        <div className="border rounded p-3 mb-3 small">
          <div className='d-sm-flex justify-content-between'>
            <div className=''>
              <div>{business.name || '—'}</div>
              {business.billingAddress && <div className="text-muted">{business.billingAddress}</div>}
              {business.currency && (
                <div className="text-muted">
                  Currency: {business.currency.code ?? business.currency.symbol ?? '—'}
                </div>
              )}
            </div>
            <div className='d-inline-block mt-4 mt-sm-0'>
              {business.logoSrc ? (
                <div className="mb-2">
                  <img
                    src={business.logoSrc}
                    alt={`${business.name || 'Business'} logo`}
                    style={{ objectFit: 'contain', width: '300px' }}
                  />
                </div>
              ): <div className='bg-light d-inline-block py-3 px-2'>NO LOGO</div>}
            </div>
          </div>
        </div>
      )}
      {isLoading && <Spinner />}
      {!isLoading && (!invoices || invoices.length === 0) && (
        <p className="text-muted">No invoices.</p>
      )}
      {!isLoading && invoices && invoices.length > 0 && (
        <div className="admin-invoices-list">
          {invoices.map((inv: any) => (
            <div key={inv._id} className="admin-invoice-card border rounded p-3 mb-3">
              <div className="d-flex flex-wrap justify-content-between align-items-baseline gap-2 mb-2">
                <strong>#{inv.code}</strong>
                <span>
                  {inv.issueDate && formatDateShort(inv.issueDate, currentUser?.locale)}
                  {' · '}
                  Due: {inv.dueDate && formatDateShort(inv.dueDate, currentUser?.locale)}
                  {' · '}
                  <span className="text-capitalize">{inv.status}</span>
                  {' · '}
                  Total: {inv.businessDetails?.currency?.symbol ?? '$'} {Number(inv.total).toFixed(2)}
                </span>
              </div>
              {inv.client && (
                <p className="small text-muted mb-2">
                  Client: {inv.client.tradingName || inv.client.email}
                </p>
              )}
              <table className="table table-sm table-borderless mb-0 small">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th className="text-end">Rate</th>
                    <th className="text-end">Qty</th>
                    <th className="text-end">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(inv.items || []).map((item: any, i: number) => (
                    <tr key={i}>
                      <td>{item.description || '—'}</td>
                      <td className="text-end">{Number(item.rate ?? 0).toFixed(2)}</td>
                      <td className="text-end">{Number(item.quantity ?? 0).toFixed(2)}</td>
                      <td className="text-end">{Number(item.amount ?? 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default AdminUserInvoices
